/**
 * XLM - Innovation & Technology | João Rodrigues | v2.0 | 2026*
 * 
 * Gestão de ficheiros do utilizador:
 * 
 * - Otimização de desempenho: substituição do cálculo de tamanho via JSON.stringify
 *   (que causava picos de alocação e GC em ficheiros >50MB) por valor pré-calculado
 *   no momento do upload (fileSize).
 * 
 * - Substituição de window.confirm() síncrono (bloqueia event loop) por modal
 *   assíncrono useAsyncConfirm.
 * 
 * - Verificação de duplicados com confirmação de substituição (delete+create).
 * - Integração com IndexedDB via UserFileCRUDL.
 */

// eslint-disable-next-line @typescript-eslint/no-unused-vars
import React, { ChangeEvent, useEffect, useState } from "react";
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { createUserFile, deleteUserFile, readSavedUserFile, listUserFile, clearAllUserFiles} from "../util/UserFileCRUDL";
import { isSavedUserFile, SavedUserFile, UserFile } from "../types/UserFile";
//import MaterialReactTable, { MRT_ColumnDef } from "material-react-table";
//import { MRT_Localization_PT } from "material-react-table/locales/pt";
import { Bicon, Button } from "../util/BootstrapIcons";
import  {useAsyncConfirm} from "../util/useAsyncConfirm";
import { ALLOWED_TYPES } from "util/constants";




// https://stackoverflow.com/a/18650828/2573422
export function formatBytes(a: number, b = 2) { if (!+a) return "0 Bytes"; const c = 0 > b ? 0 : b, d = Math.floor(Math.log(a) / Math.log(1024)); return `${parseFloat((a / Math.pow(1024, d)).toFixed(c))} ${["Bytes", "KiB", "MiB", "GiB", "TiB", "PiB", "EiB", "ZiB", "YiB"][d]}` }

//const intl = new Intl.DateTimeFormat(["pt", "en"], { dateStyle: "short", timeStyle: "medium" });

/*const cols: MRT_ColumnDef<SavedUserFile>[] = [
    {
        header: "Ficheiros Locais",
        Header: <><i className="bi bi-file-earmark-fill"></i> Ficheiros Locais</>,
        accessorKey: "name",
        size: 80,
        Cell: ({ row }) => <Button i="file-earmark" className="text-nowrap text-primary btn m-0 p-0" title={`Abrir ${row.original.name}`} text={row.original.name} />
    },
    {

        header:"Tamanho",
        accessorFn: (file:any) => formatBytes(file.fileSize || 0)
    },
    {
        header: "N.º de Entidades / Ocorrências",
        accessorFn: (file: any) => `${file.ents.length} / ${file.ents.reduce((acc: number, c: any) => acc + c.offsets.length, 0)}`
    },
    {
        header: "Importado", accessorKey: "imported", accessorFn: (file:any) => intl.format(new Date(file.imported))
    },
    {
        header: "Modificado", accessorKey: "modified", accessorFn: (file:any) => intl.format(new Date(file.modified))
    }
]*/


export default function SelectFile({ setUserFile }: { setUserFile: (file: UserFile) => void }) {
    const { askUser, notify,ConfirmComponent } = useAsyncConfirm();
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [list, setList] = useState<SavedUserFile[]>([]);
    


    return (
            <div className="container my-3">
                <div className="d-flex justify-content-center">
                    <AddUserFileAction setUserFile={setUserFile} askUser={askUser} notify={notify}/>
                </div>
                {ConfirmComponent}
            </div>
        );


}

async function onFile(event: React.ChangeEvent<HTMLInputElement>,confirmFn: (message: string) => Promise<boolean>,
notifyFn: (message: string) => Promise<boolean>): Promise<UserFile | undefined> {
    await clearAllUserFiles();

    let files = event.target.files;
    if (files == null || files.length === 0) return;

    let file = files[0];
    if (!ALLOWED_TYPES.includes(file.type) && !file.name.endsWith('.txt') && !file.name.endsWith('.doc')) {
        await notifyFn("Formato não suportado. Use PDF, DOCX, DOC ou TXT.");
        event.target.value = "";
        return;
    }
    let formData = new FormData();
    formData.append("file", file);

    if (file.type === "application/json") {
        let text = await file.text();
        try {

            const obj = await new Promise((resolve,reject) => {
                try{
                   setTimeout(() => resolve(JSON.parse(text)),0);
                }
                catch(e){
                    reject(e);
                }
            });

            if (isSavedUserFile(obj)) {
                let savedUserFile = await readSavedUserFile(obj.name);
                if (savedUserFile !== undefined) {
           
                    const sms ="Existe um ficheiro guardado localmente com o mesmo nome. Confirma que quer apagar ficheiro antigo?";
                    const usrConfirm = await confirmFn(sms);
                    
                    if (!usrConfirm) {
                        event.target.value = "";
                        return;
                    }
                    await deleteUserFile(savedUserFile);
                }
                
                await createUserFile(obj);
                return new UserFile(obj);
            }
        } catch (e) {
            if (process.env.REACT_APP_ENV === 'dev' || process.env.REACT_APP_ENV === 'qa') {
                console.error("Erro ao fazer parse do JSON: " + file.name, e);
            }
            return undefined;
        }
        return undefined;
    }

    // Fluxo para PDF/HTML (Novos processos)
    event.target.disabled = true;
    try {
        const r = await fetch("./html", { method: "POST", body: formData });
        let content = await r.text();
        if (r.status !== 200) throw new Error(content);

        let documentDom = new DOMParser().parseFromString(content, "text/html");
        const newUserFile = UserFile.newFrom(file.name, documentDom.body.innerHTML);
        
     
        await createUserFile(newUserFile.toSavedFile());
        return newUserFile;
    } catch (e) {
        if(process.env.NODE_ENV === 'dev' || process.env.NODE_ENV === "qa") {
            console.error("Falha ao interpretar ficheiro: " + file.name, e);
        }
        await notifyFn("Falha ao interpretar ficheiro submetido.");
        return undefined;
    } finally {
        event.target.value = "";
        event.target.disabled = false;
    }
}

export function AddUserFileAction({ setUserFile,askUser,notify }: { 
    setUserFile: (file: UserFile) => void,
    askUser: (message: string) => Promise<boolean>,
    notify: (message: string) => Promise<boolean>
}) {
    const [uploading, setUploading] = useState<boolean>(false);
    const onChange = async (e: ChangeEvent<HTMLInputElement>) => {
        setUploading(true);
        const f = await onFile(e,askUser,notify);
        if (f) setUserFile(f);
        setUploading(false);
    }
    return <>
        <label htmlFor="file" role="button" className={`btn btn-primary m-auto ${uploading ? "disabled" : ""}`}>
            {uploading ? <><span className="spinner-border spinner-border-sm" role="status"></span> A carregar ficheiro...</> : <><Bicon n="file-earmark-plus" /> Adicionar Ficheiro</>}
        </label>
        <input hidden type="file" name="file" id="file" onChange={onChange}></input>
    </>
}

export function UserFileActions(props: { file: SavedUserFile, setUserFile: (file: UserFile) => void }) {
    return <Button className="m-1 p-1 text-danger btn" title="Eliminar" onClick={async (ev) => { 
        ev.stopPropagation(); 
        await deleteUserFile(props.file); 
    }} i="trash" />
}

